<template>
    <div class="dsd-root" data-testid="dsd-root">
        <div
            v-if="store.detailLoading"
            class="dsd-state dsd-state--loading"
            data-testid="dsd-loading"
        >
            Loading set...
        </div>
        <div
            v-else-if="store.detailError"
            class="dsd-state dsd-state--error"
            data-testid="dsd-error"
        >
            {{ store.detailError }}
        </div>
        <template v-else-if="set">
            <header class="dsd-header" data-testid="dsd-header">
                <div class="dsd-header__main">
                    <div class="d-flex align-center">
                        <h2 class="dsd-header__title">{{ set.name }}</h2>
                        <HelpIcon
                            audience="owner"
                            slug="decoration-sets"
                            heading="change-a-set-you-already-have"
                        />
                    </div>
                    <p
                        v-if="set.description"
                        class="dsd-header__desc"
                    >
                        {{ set.description }}
                    </p>
                    <div class="dsd-header__meta">
                        <span
                            v-if="set.client_tag"
                            class="dsd-chip"
                            data-testid="dsd-client-tag"
                        >
                            {{ set.client_tag }}
                        </span>
                        <span class="dsd-header__audit">
                            Updated {{ formatDate(set.updated_at) }}
                        </span>
                    </div>
                </div>
                <div class="dsd-header__actions">
                    <button
                        type="button"
                        class="dsd-btn dsd-btn--ghost"
                        data-testid="dsd-action-edit"
                        @click="onEdit"
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        class="dsd-btn dsd-btn--ghost"
                        data-testid="dsd-action-duplicate"
                        :disabled="duplicating"
                        @click="onDuplicate"
                    >
                        Duplicate
                    </button>
                    <button
                        type="button"
                        class="dsd-btn dsd-btn--ghost dsd-btn--danger"
                        data-testid="dsd-action-delete"
                        @click="onDelete"
                    >
                        Delete
                    </button>
                    <!--
                        Issue #2812: a disabled "Save new version"
                        button lived here. It advertised a version-fork
                        capability that does not exist. Restore it when
                        the version-stack work actually ships.
                    -->
                </div>
            </header>

            <p
                v-if="actionError"
                class="dsd-error"
                data-testid="dsd-action-error"
            >
                {{ actionError }}
            </p>

            <section class="dsd-section">
                <h3 class="dsd-section__title">Placements</h3>
                <div
                    v-if="!set.placements || !set.placements.length"
                    class="dsd-state dsd-state--empty"
                    data-testid="dsd-placements-empty"
                >
                    No placements defined yet.
                </div>
                <table
                    v-else
                    class="dsd-table"
                    data-testid="dsd-placements-table"
                >
                    <thead>
                        <tr>
                            <th>Zone</th>
                            <th>Artwork</th>
                            <th>Imprint size</th>
                            <th>Perso fields</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="p in set.placements"
                            :key="p.id"
                            :data-testid="`dsd-placement-row-${p.id}`"
                        >
                            <td :data-testid="`dsd-placement-zone-${p.id}`">
                                Zone #{{ p.placement_zone_id }}
                            </td>
                            <td :data-testid="`dsd-placement-artwork-${p.id}`">
                                Artwork #{{ p.artwork_id }}
                            </td>
                            <td :data-testid="`dsd-placement-size-${p.id}`">
                                {{ formatSize(p) }}
                            </td>
                            <td :data-testid="`dsd-placement-perso-${p.id}`">
                                {{ formatPersoKeys(p) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section class="dsd-section">
                <h3 class="dsd-section__title">Where used</h3>
                <div
                    class="dsd-state dsd-state--empty"
                    data-testid="dsd-where-used"
                >
                    No applications recorded yet. Apply-history tracking
                    lands in a follow-up alongside the bulk-apply audit.
                </div>
            </section>
        </template>
        <div
            v-else
            class="dsd-state dsd-state--empty"
            data-testid="dsd-empty"
        >
            Set not found.
        </div>

        <DecorationSetEditModal
            v-if="set"
            v-model="showEditModal"
            :set-id="id"
            @saved="onEditSaved"
        />
    </div>
</template>

<script>
/**
 * DecorationSetDetailPage.vue -- /distributor/decorations/sets/:id.
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase 4.8 -- set detail surface.
 *
 * Read-only view of a decoration set: header (name + description +
 * client tag + audit), placements table, where-used placeholder, and
 * the four action buttons.
 *
 * Action button gating
 * --------------------
 * - **Edit**: live (Phase 4.10). Opens DecorationSetEditModal which
 *   lets the operator edit metadata + the full placement list
 *   (add / remove / reorder / edit zone, artwork, sizing, perso fields).
 *   On save, refetches the detail so the rendered ordering reflects the
 *   server-authoritative state.
 * - **Duplicate**: live. POSTs to /decoration_sets/<id>/duplicate/
 *   (collision-safe naming handled server-side) and navigates to the
 *   new set's detail page on success.
 * - **Delete**: live. The backend doesn't expose a DELETE endpoint
 *   today -- the closest path is PATCH ``is_active=false`` (soft
 *   delete via the PATCH metadata surface). For P4.8 the button is
 *   wired to surface a confirm dialog + PATCH; the followup boulder
 *   will graduate to a hard-delete endpoint if Jake decides we need
 *   one.
 * - **Save new version**: not rendered (issue #2812). The button was
 *   a disabled placeholder advertising a create-fork flow that does
 *   not exist; it returns when the version-stack work ships.
 *
 * Where-used: ADR 0044 reserves an apply-audit table. P4.1 didn't
 * ship it. Until the audit table lands the section renders a
 * placeholder copy. Follow-up boulder: surface apply history per
 * product + per client.
 *
 * Capability: distributor.decoration_library.read enforced at the
 * router.
 */
import api from '@/services/api'
import { useDecorationSetsStore } from '@/stores/decorationSets'
import DecorationSetEditModal from './DecorationSetEditModal.vue'
import HelpIcon from '@/components/Help/HelpIcon.vue'

export default {
    name: 'DecorationSetDetailPage',

    components: { HelpIcon, DecorationSetEditModal },

    props: {
        // The route declares ``props: true``, so :id arrives here as a
        // string from the URL. We coerce defensively in case a parent
        // passes it programmatically as a number.
        id: {
            type: [String, Number],
            required: true,
        },
    },

    data() {
        return {
            store: useDecorationSetsStore(),
            duplicating: false,
            actionError: null,
            showEditModal: false,
        }
    },

    computed: {
        set() {
            return this.store.detail
        },
    },

    async mounted() {
        await this.store.fetchDetail(this.id)
    },

    beforeUnmount() {
        // Clear the slot so a stale set from a previous detail view
        // doesn't flash through during the next navigation. The list
        // store stays warm.
        this.store.clearDetail()
    },

    methods: {
        formatDate(iso) {
            if (!iso) return ''
            try {
                const d = new Date(iso)
                if (Number.isNaN(d.getTime())) return iso
                return d.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                })
            } catch (_e) {
                return iso
            }
        },

        formatSize(placement) {
            const w = placement?.imprint_width_in
            const h = placement?.imprint_height_in
            if (w == null || h == null) return '-'
            return `${w} in x ${h} in`
        },

        formatPersoKeys(placement) {
            const keys = placement?.perso_field_keys
            if (!Array.isArray(keys) || !keys.length) return 'None'
            return keys.join(', ')
        },

        onEdit() {
            this.actionError = null
            this.showEditModal = true
        },

        async onEditSaved() {
            // The edit modal updates the store's ``detail`` slot
            // optimistically; re-fetching gives us authoritative server
            // state (ordering, computed fields, audit timestamps).
            await this.store.fetchDetail(this.id)
        },

        async onDuplicate() {
            if (this.duplicating) return
            this.duplicating = true
            this.actionError = null
            try {
                const res = await api.post(
                    `/decoration_sets/${this.id}/duplicate/`,
                    {},
                )
                const created = res?.data || null
                if (created?.id != null) {
                    this.$router.push(
                        `/distributor/decorations/sets/${created.id}`,
                    )
                } else {
                    this.actionError =
                        'Duplicate succeeded but no new id was returned.'
                }
            } catch (e) {
                this.actionError =
                    e?.response?.data?.error ||
                    e?.message ||
                    'Duplicate failed.'
            } finally {
                this.duplicating = false
            }
        },

        async onDelete() {
            // The backend's only delete-shaped surface today is
            // PATCH is_active=false. Confirm + soft-delete; navigate
            // back to the list on success.
            const ok =
                typeof window !== 'undefined' &&
                typeof window.confirm === 'function'
                    ? window.confirm(
                          'Archive this set? It will no longer appear in the list.',
                      )
                    : true
            if (!ok) return
            this.actionError = null
            try {
                await api.patch(`/decoration_sets/${this.id}/`, {
                    is_active: false,
                })
                this.$router.push('/distributor/decorations/sets')
            } catch (e) {
                this.actionError =
                    e?.response?.data?.error ||
                    e?.message ||
                    'Delete failed.'
            }
        },
    },
}
</script>

<style scoped>
.dsd-root {
    width: 100%;
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.dsd-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    padding-bottom: 14px;
}

.dsd-header__title {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
}

.dsd-header__desc {
    margin: 6px 0 0;
    color: rgba(0, 0, 0, 0.7);
    font-size: 14px;
}

.dsd-header__meta {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
}

.dsd-header__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.dsd-chip {
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 10px;
    background: rgba(25, 118, 210, 0.12);
    color: #1565c0;
}

.dsd-section__title {
    margin: 0 0 8px;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.55);
}

.dsd-state {
    padding: 32px 24px;
    text-align: center;
    color: rgba(0, 0, 0, 0.55);
    border: 1px dashed rgba(0, 0, 0, 0.15);
    border-radius: 6px;
}

.dsd-state--error {
    color: #d32f2f;
    border-color: rgba(211, 47, 47, 0.4);
}

.dsd-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 6px;
    overflow: hidden;
}

.dsd-table th,
.dsd-table td {
    text-align: left;
    padding: 10px 14px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    font-size: 14px;
}

.dsd-table th {
    background: #f5f7fa;
    font-weight: 600;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.7);
}

.dsd-table tr:last-child td {
    border-bottom: 0;
}

.dsd-btn {
    border-radius: 4px;
    border: 1px solid rgba(25, 118, 210, 0.4);
    background: #fff;
    color: #1976d2;
    padding: 6px 12px;
    font-size: 13px;
    cursor: pointer;
}

.dsd-btn--ghost {
    background: #fff;
}

.dsd-btn--danger {
    border-color: rgba(211, 47, 47, 0.5);
    color: #c62828;
}

.dsd-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.dsd-error {
    margin: 0;
    color: #d32f2f;
    font-size: 13px;
}
</style>
